import { useEffect, type CSSProperties, type ReactNode } from "react";
import type { TerminalConfig, TerminalMessage } from "../types";
import type { TerminalCommand } from "../commands/types";
import {
  useTerminalController,
  type TerminalController,
} from "../hooks/useTerminalController";
import { TerminalThemeProvider, useTerminalClasses } from "../theme/context";
import { tokensToStyle } from "../theme/tokens";
import { cx } from "../util/cx";
import { StatusBar } from "./StatusBar";
import { TerminalPanel } from "./TerminalPanel";

/** Render-prop overrides for whole regions of the terminal. */
export interface TerminalSlots {
  /** Replace the collapsed status bar entirely. */
  statusBar?: (controller: TerminalController) => ReactNode;
  /** Replace the open panel's header. */
  header?: (controller: TerminalController) => ReactNode;
  /** Replace how a single message renders. */
  renderMessage?: (message: TerminalMessage) => ReactNode;
  /** Rendered when there are no messages. */
  empty?: () => ReactNode;
}

export interface ReactTerminalProps {
  config: TerminalConfig;
  /** Custom commands merged on top of the built-ins. */
  commands?: TerminalCommand[];
  /** Region overrides. */
  slots?: TerminalSlots;
  /** Extra class on the root. */
  className?: string;
  /** Receives the live controller for imperative use (open/close/submit). */
  onReady?: (controller: TerminalController) => void;
}

export function ReactTerminal(props: ReactTerminalProps) {
  return (
    <TerminalThemeProvider theme={props.config.theme}>
      <TerminalShell {...props} />
    </TerminalThemeProvider>
  );
}

function TerminalShell({
  config,
  commands,
  slots,
  className,
  onReady,
}: ReactTerminalProps) {
  const classes = useTerminalClasses();
  const controller = useTerminalController({ config, commands });
  // Docked: the host gives the terminal a box to fill, so there is nothing to collapse into
  // and nothing to resize — the panel is simply always open. See `TerminalLayout`.
  const docked = config.layout === "docked";
  const open = docked || controller.isOpen;

  useEffect(() => {
    onReady?.(controller);
    // Fire once with the initial controller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global toggle hotkey. Not registered when docked: there is no collapsed state to toggle
  // into, so the binding would swallow the key and do nothing visible.
  useEffect(() => {
    const hotkey = config.hotkey === undefined ? "ctrl+`" : config.hotkey;
    if (docked || !hotkey || typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if (matchesHotkey(e, hotkey)) {
        e.preventDefault();
        controller.toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [config.hotkey, controller, docked]);

  const rootStyle = tokensToStyle(
    config.theme?.tokens,
  ) as unknown as CSSProperties;

  return (
    <div
      className={cx(
        classes.root,
        docked && "rt-root--docked",
        className,
        config.theme?.colorScheme && `rt-scheme-${config.theme.colorScheme}`,
      )}
      style={rootStyle}
      data-open={open}
      data-layout={docked ? "docked" : "floating"}
    >
      {open && (
        <TerminalPanel
          controller={controller}
          docked={docked}
          header={slots?.header}
          renderMessage={slots?.renderMessage}
          empty={slots?.empty}
        />
      )}
      {/* The status bar IS the collapsed state, so a docked terminal has no use for it. */}
      {!docked &&
        (slots?.statusBar ? (
          slots.statusBar(controller)
        ) : (
          <StatusBar controller={controller} />
        ))}
    </div>
  );
}

function matchesHotkey(e: KeyboardEvent, hotkey: string): boolean {
  const parts = hotkey
    .toLowerCase()
    .split("+")
    .map((p) => p.trim());
  const key = parts[parts.length - 1];
  const needCtrl = parts.includes("ctrl");
  const needMeta = parts.includes("meta") || parts.includes("cmd");
  const needAlt = parts.includes("alt");
  const needShift = parts.includes("shift");
  const needMod = parts.includes("mod");

  if (needCtrl && !e.ctrlKey) return false;
  if (needMeta && !e.metaKey) return false;
  if (needAlt !== e.altKey) return false;
  if (needShift !== e.shiftKey) return false;
  if (needMod && !(e.ctrlKey || e.metaKey)) return false;

  return e.key.toLowerCase() === key;
}
